/**
 * SnapshotFieldsForm Component
 *
 * Dynamic form component for capturing market snapshot field values.
 * Renders appropriate input controls based on field type configuration
 * from the MarketStateDraft's fieldConfigSnapshot.
 *
 * This is a content-only component - the parent provides any Card/Modal chrome.
 *
 * Supports 6 field types:
 * - text: Single-line text input
 * - number: Numeric input with validation
 * - dropdown: Single-select from predefined options
 * - checkbox: Boolean toggle
 * - url: Text input with https:// validation
 * - image: Image capture with paste/drag-drop/file picker support
 */

import React, { useCallback } from 'react';

// Internal absolute imports
import {
  Input,
  NumberInput,
  Select,
  SelectItem,
  Checkbox,
  EmptyState,
  Link,
  Button,
} from '@enfomentis/ui';

// Internal relative imports
import { ImageFieldInput } from './ImageFieldInput';

// Types
import type { MarketStateDraft } from '@/types/transient/MarketStateDraft';
import type { ImageReference } from '@/types/schema/StoredImage';

// =============================================================================
// Types
// =============================================================================

/**
 * Field configuration from MarketStateDraft.fieldConfigSnapshot
 * Reuse existing type to avoid drift
 */
export type SnapshotFieldConfig = MarketStateDraft['fieldConfigSnapshot'][number];

/**
 * Value type for snapshot fields
 * Matches MarketStateDraft.snapshots[].data value types
 */
export type SnapshotFieldValue =
  | string
  | number
  | boolean
  | ImageReference
  | null
  | undefined;

/**
 * Props for SnapshotFieldsForm component
 */
export interface SnapshotFieldsFormProps {
  /** Field configurations from fieldConfigSnapshot */
  fields: SnapshotFieldConfig[];
  /** Current field values (fieldId → value) */
  values: Partial<Record<string, SnapshotFieldValue>>;
  /** Callback when any field value changes */
  onValuesChange: (values: Partial<Record<string, SnapshotFieldValue>>) => void;
  /** Validation errors by fieldId (optional, defaults to {}) */
  validationErrors?: Partial<Record<string, string>>;
  /** Whether settings failed to load (shows error empty state) */
  loadError?: boolean;
  /** Callback to retry loading settings (shows Retry button when provided with loadError) */
  onRetry?: (() => void) | undefined;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Normalize HeroUI NumberInput value to our expected type.
 * NumberInput's onValueChange can return number | number[] | null,
 * but we only expect single number values.
 */
const normalizeNumberValue = (
  val: number | number[] | null | undefined
): number | undefined => {
  if (val === null || val === undefined) return undefined;
  if (Array.isArray(val)) return undefined;
  if (Number.isNaN(val)) return undefined;
  return val;
};

/**
 * Normalize HeroUI Select value to our expected type.
 * Select's onSelectionChange returns a Set, but we store single string values.
 * Empty Set = undefined (clearable behavior).
 */
const normalizeSelectValue = (
  keys: 'all' | Set<React.Key>
): string | undefined => {
  if (keys === 'all') return undefined;
  if (keys.size === 0) return undefined;
  if (keys.size > 1) return undefined;
  const firstKey = keys.values().next().value;
  return typeof firstKey === 'string' ? firstKey : undefined;
};

/**
 * Check if a value is an ImageReference
 */
const isImageReference = (value: SnapshotFieldValue): value is ImageReference => {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'type' in value &&
    value.type === 'image'
  );
};

// =============================================================================
// Component
// =============================================================================

/**
 * SnapshotFieldsForm Component
 *
 * Renders dynamic form fields based on field configuration.
 * Shows empty state when no fields are configured.
 */
export const SnapshotFieldsForm: React.FC<SnapshotFieldsFormProps> = ({
  fields,
  values,
  onValuesChange,
  validationErrors = {},
  loadError = false,
  onRetry,
}) => {
  /**
   * Handle value change for a specific field
   */
  const handleValueChange = useCallback(
    (fieldId: string, value: SnapshotFieldValue) => {
      onValuesChange({
        ...values,
        [fieldId]: value,
      });
    },
    [values, onValuesChange]
  );

  // ===========================================================================
  // Field Renderers
  // ===========================================================================

  /**
   * Render a text input field
   */
  const renderTextField = (field: SnapshotFieldConfig) => {
    const currentValue = values[field.fieldId];
    const displayValue = typeof currentValue === 'string' ? currentValue : '';
    const error = validationErrors[field.fieldId];

    return (
      <div key={field.fieldId} className="w-full">
        <Input
          label={field.name}
          description={field.description}
          value={displayValue}
          onValueChange={(val) => handleValueChange(field.fieldId, val)}
          isInvalid={!!error}
          errorMessage={error}
          classNames={{
            input: 'text-base',
            inputWrapper: 'min-h-[44px]',
          }}
        />
      </div>
    );
  };

  /**
   * Render a number input field
   */
  const renderNumberField = (field: SnapshotFieldConfig) => {
    const currentValue = values[field.fieldId];
    const numericValue =
      typeof currentValue === 'number' ? currentValue : undefined;
    const error = validationErrors[field.fieldId];

    return (
      <div key={field.fieldId} className="w-full">
        <NumberInput
          label={field.name}
          description={field.description}
          value={numericValue}
          onValueChange={(val) =>
            handleValueChange(field.fieldId, normalizeNumberValue(val))
          }
          hideStepper
          isInvalid={!!error}
          errorMessage={error}
          classNames={{
            input: 'text-base',
            inputWrapper: 'min-h-[44px]',
          }}
        />
      </div>
    );
  };

  /**
   * Render a dropdown/select field
   */
  const renderDropdownField = (field: SnapshotFieldConfig) => {
    const currentValue = values[field.fieldId];
    const stringValue =
      typeof currentValue === 'string' ? currentValue : undefined;
    const options = field.options ?? [];
    const hasOptions = options.length > 0;
    const error = validationErrors[field.fieldId];

    return (
      <div key={field.fieldId} className="w-full">
        <Select
          label={field.name}
          description={field.description}
          placeholder={hasOptions ? 'Select an option' : 'No options configured'}
          selectedKeys={stringValue ? new Set([stringValue]) : new Set()}
          onSelectionChange={(keys) =>
            handleValueChange(field.fieldId, normalizeSelectValue(keys))
          }
          isDisabled={!hasOptions}
          isInvalid={!!error}
          errorMessage={error}
          classNames={{
            trigger: 'min-h-[44px]',
            value: 'text-base',
          }}
        >
          {options.map((option) => (
            <SelectItem key={option.id}>{option.label}</SelectItem>
          ))}
        </Select>
      </div>
    );
  };

  /**
   * Render a checkbox field
   */
  const renderCheckboxField = (field: SnapshotFieldConfig) => {
    const currentValue = values[field.fieldId];
    const isSelected = currentValue === true;
    const error = validationErrors[field.fieldId];

    return (
      <div key={field.fieldId} className="w-full">
        <Checkbox
          isSelected={isSelected}
          onValueChange={(checked) => handleValueChange(field.fieldId, checked)}
          classNames={{
            base: 'min-h-[44px] items-start',
            wrapper: 'mt-1',
          }}
        >
          <div className="flex flex-col">
            <span className="text-base">{field.name}</span>
            {field.description && (
              <span className="text-sm text-text-tertiary">
                {field.description}
              </span>
            )}
          </div>
        </Checkbox>
        {error && (
          <p className="text-tiny text-danger mt-1">{error}</p>
        )}
      </div>
    );
  };

  /**
   * Render a URL input field with validation error display
   */
  const renderUrlField = (field: SnapshotFieldConfig) => {
    const currentValue = values[field.fieldId];
    const displayValue = typeof currentValue === 'string' ? currentValue : '';
    const error = validationErrors[field.fieldId];

    return (
      <div key={field.fieldId} className="w-full">
        <Input
          label={field.name}
          description={field.description}
          placeholder="https://"
          value={displayValue}
          onValueChange={(val) => handleValueChange(field.fieldId, val)}
          isInvalid={!!error}
          errorMessage={error}
          classNames={{
            input: 'text-base',
            inputWrapper: 'min-h-[44px]',
          }}
        />
      </div>
    );
  };

  /**
   * Render an image field with paste/drag-drop/file picker support
   */
  const renderImageField = (field: SnapshotFieldConfig) => {
    const currentValue = values[field.fieldId];
    const imageRef = isImageReference(currentValue) ? currentValue : null;
    const error = validationErrors[field.fieldId];

    return (
      <div key={field.fieldId} className="w-full">
        <ImageFieldInput
          fieldId={field.fieldId}
          value={imageRef}
          onChange={(newValue) => handleValueChange(field.fieldId, newValue)}
          label={field.name}
          description={field.description}
          error={error}
        />
      </div>
    );
  };

  /**
   * Render a field based on its type
   */
  const renderField = (field: SnapshotFieldConfig) => {
    switch (field.type) {
      case 'text':
        return renderTextField(field);
      case 'number':
        return renderNumberField(field);
      case 'dropdown':
        return renderDropdownField(field);
      case 'checkbox':
        return renderCheckboxField(field);
      case 'url':
        return renderUrlField(field);
      case 'image':
        return renderImageField(field);
      default:
        return null;
    }
  };

  // ===========================================================================
  // Render
  // ===========================================================================

  // Empty state when no fields configured or load error
  if (fields.length === 0) {
    return (
      <EmptyState
        title={loadError ? 'Failed to load snapshot fields' : 'No snapshot fields configured'}
        description={
          loadError
            ? 'There was a problem loading your field configuration. You can still create a snapshot, but no fields will be available.'
            : 'Configure snapshot fields in Settings to capture market analysis data'
        }
        action={
          <div className="flex gap-2">
            {loadError && onRetry && (
              <Button size="sm" variant="flat" onPress={onRetry}>
                Retry
              </Button>
            )}
            <Link
              href="/settings/workflow/market-snapshots"
              color="primary"
              size="sm"
            >
              Configure Fields
            </Link>
          </div>
        }
      />
    );
  }

  // Fields form
  return (
    <div className="space-y-4">
      {fields.map((field) => renderField(field))}
    </div>
  );
};

export default SnapshotFieldsForm;
